import React, { useRef, useEffect, useState } from 'react';
import {
  FollowProps, HashtagProps, MentionProps, OnLoadFnType, ShareProps, TimelineProps, TweetProps,
} from '@/tracking/twitter/types';
import {
  canUseDOM,
  useShallowCompareMemoize,
  removeChildrenWithAttribute,
  cloneShallow, useTwFidgetFactory,
} from './utils';

const childDivIdentifyingAttribute = 'twdiv';

function useTwitterWidget(factoryFunctionName: string, primaryArg: any, options?: Object, onLoad?: OnLoadFnType) {
  const [error, setError] = useState<unknown | null>(null);

  const ref = useRef<HTMLDivElement>(null);

  // noop if ssr
  if (!canUseDOM) {
    return { ref, error };
  }

  const widgets = useTwFidgetFactory();

  // Make deps for useEffect.
  // options, and possibly primaryArg, are objects that should be compared (shallow).
  // There currently aren't any nested arrays or objects, so they
  // can be cloned in a shallow manner.
  // NOTE onLoad is used in useCallback, but it is not listed as a dependency.
  // Listing it would likely cause unnecessary loads. The latest onLoad should be
  // used regardless, since it will not be called unless the other dependencies
  // change, so it works out.
  const deps = [
    factoryFunctionName,
    useShallowCompareMemoize(primaryArg),
    useShallowCompareMemoize(options),
    widgets,
  ];

  useEffect(() => {
    // Reset error
    setError(null);

    // Protect against race conditions
    // (set to true in cleanup function;
    // checked if canceled in async loadWidget)
    let isCanceled = false;

    if (ref.current && widgets) {
      removeChildrenWithAttribute(ref.current, childDivIdentifyingAttribute);

      // eslint-disable-next-line no-inner-declarations
      async function loadWidget() {
        if (!ref || !ref.current) {
          return;
        }

        const childEl = document.createElement('div');
        childEl.setAttribute(childDivIdentifyingAttribute, 'yes');
        ref.current.appendChild(childEl);

        try {
          // primaryArg (possibly an object) and options must be cloned
          // since twitter mutates them (gah!).
          // There currently aren't any nested arrays or objects, so they
          // can be cloned in a shallow manner.
          const resultMaybe = await widgets[factoryFunctionName](
            cloneShallow(primaryArg),
            childEl,
            cloneShallow(options),
          );

          // Twitter returns undefined if widget creation fails.
          // However, if deps are stale (isCanceled), suppress error (likely race condition).
          if (!resultMaybe && !isCanceled) {
            throw new Error(
              'Twitter could not create widget. If it is a Timeline or '
              + 'Tweet, ensure the screenName/tweetId exists.',
            );
          }
        } catch (e) {
          console.error(e);
          setError(e);
          return;
        }

        if (!ref || !ref.current) {
          return;
        }

        if (isCanceled) {
          if (childEl) {
            childEl.remove();
          }
          return;
        }

        if (onLoad) {
          onLoad();
        }
      }

      loadWidget().catch(() => {});
    }

    return () => {
      isCanceled = true;
    };
  }, deps);

  return { ref, error };
}

export const Follow = ({
  username, options, onLoad, renderError,
}: FollowProps) => {
  const { ref, error } = useTwitterWidget(
    'createFollowButton',
    username,
    options,
    onLoad,
  );
  return <div ref={ref}>{!!error && renderError && renderError(error)}</div>;
};

export const Hashtag = ({
  hashtag, options, onLoad, renderError,
}: HashtagProps) => {
  const { ref, error } = useTwitterWidget(
    'createHashtagButton',
    hashtag,
    options,
    onLoad,
  );
  return <div ref={ref}>{!!error && renderError && renderError(error)}</div>;
};

export const Mention = ({
  username, options, onLoad, renderError,
}: MentionProps) => {
  const { ref, error } = useTwitterWidget(
    'createMentionButton',
    username,
    options,
    onLoad,
  );
  return <div ref={ref}>{!!error && renderError && renderError(error)}</div>;
};

export const Share = ({
  url, options, onLoad, renderError,
}: ShareProps) => {
  const { ref, error } = useTwitterWidget(
    'createShareButton',
    url,
    options,
    onLoad,
  );
  return <div ref={ref}>{!!error && renderError && renderError(error)}</div>;
};

export const Timeline = ({
  dataSource, options, onLoad, renderError,
}: TimelineProps) => {
  const { ref, error } = useTwitterWidget(
    'createTimeline',
    dataSource,
    options,
    onLoad,
  );
  return <div ref={ref}>{!!error && renderError && renderError(error)}</div>;
};

export const Tweet = ({
  tweetId, options, onLoad, renderError,
}: TweetProps) => {
  const { ref, error } = useTwitterWidget(
    'createTweet',
    tweetId,
    options,
    onLoad,
  );
  return <div ref={ref}>{!!error && renderError && renderError(error)}</div>;
};
